import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Popconfirm, Space, Table, Tag, message } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { adminApi, type PageResult } from '../../api'
import { PageHeader, tablePagination, useServerPagination } from '../../components/ui'
import { AgentManageTabs } from './AgentManageTabs'
import './AgentSettings.css'

export type AiSkillRow = {
  id: number
  skill_code: string
  skill_name: string
  description: string
  sort_no: number
  is_active: boolean
}

export function AgentSkillList() {
  const navigate = useNavigate()
  const { page, pageSize, handlePaginationChange, paginationOptions } = useServerPagination()
  const [total, setTotal] = useState(0)
  const [rows, setRows] = useState<AiSkillRow[]>([])
  const [loading, setLoading] = useState(false)

  const load = useCallback((p: number, ps: number) => {
    setLoading(true)
    return adminApi
      .get<PageResult<AiSkillRow>>('/admin/agents/skills', { params: { page: p, page_size: ps } })
      .then((r) => {
        setRows(r.data.items)
        setTotal(r.data.total)
      })
      .catch((e) => message.error(e.message))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    void load(page, pageSize)
  }, [page, pageSize, load])

  const deleteSkill = (row: AiSkillRow) => {
    adminApi
      .delete(`/admin/agents/skills/${row.id}`)
      .then(() => {
        message.success('已删除')
        void load(page, pageSize)
      })
      .catch((e) => message.error(e.message))
  }

  return (
    <>
      <AgentManageTabs />
      <div className="agent-list-head">
        <PageHeader
          title="Skill 库"
          description="可复用提示词片段。在智能体提示词中插入 {{skill:标识}}，运行时自动展开为对应正文。"
        />
        <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/manage/agents/skills/new')}>
          新建 Skill
        </Button>
      </div>

      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={rows}
        pagination={tablePagination(page, total, handlePaginationChange, paginationOptions)}
        columns={[
          {
            title: '名称',
            dataIndex: 'skill_name',
            render: (name: string, row: AiSkillRow) => (
              <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/manage/agents/skills/${row.id}`)}>
                {name}
              </Button>
            ),
          },
          {
            title: '标识',
            dataIndex: 'skill_code',
            width: 200,
            render: (code: string) => <code>{code}</code>,
          },
          { title: '说明', dataIndex: 'description', ellipsis: true, render: (v: string) => v || '—' },
          { title: '排序', dataIndex: 'sort_no', width: 72 },
          {
            title: '状态',
            dataIndex: 'is_active',
            width: 72,
            render: (v: boolean) => (v ? <Tag color="green">启用</Tag> : <Tag>停用</Tag>),
          },
          {
            title: '操作',
            width: 140,
            render: (_: unknown, row: AiSkillRow) => (
              <Space size="small">
                <Button size="small" onClick={() => navigate(`/manage/agents/skills/${row.id}`)}>
                  编辑
                </Button>
                <Popconfirm title="确定删除该 Skill？" onConfirm={() => deleteSkill(row)}>
                  <Button size="small" danger>
                    删除
                  </Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />
    </>
  )
}
